import React, { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, Loader2, Mail, AlertTriangle, CheckCircle2, MinusCircle, Send } from "lucide-react";
import ConsoleShell, { Breadcrumb } from "./ConsoleShell";
import { API } from "./AuthContext";

const STATUS = {
  sent:    { Icon: CheckCircle2, cls: "text-[#3F6D55] bg-[#EAF3EC] border-[#CFE2D2]", label: "Email sent" },
  failed:  { Icon: AlertTriangle, cls: "text-[#C84C21] bg-[#FBE7DF] border-[#F2C9B6]", label: "Email failed" },
  skipped: { Icon: MinusCircle,  cls: "text-[#9A6B1A] bg-[#FDF7E6] border-[#E8D08A]", label: "Email not configured" },
};

function StatusChip({ s }) {
  const meta = STATUS[s.email_status] || { Icon: MinusCircle, cls: "text-[#5C6470] bg-[#FAF7F1] border-[#E8E1D2]", label: "No delivery record" };
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.14em] ${meta.cls}`} data-testid={`request-status-${s.id}`}>
      <meta.Icon size={11} /> {meta.label}
    </span>
  );
}

function EmailConfigPanel({ cfg }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!cfg) return null;
  const runTest = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/contacts/test-notification`);
      setResult(data);
    } catch (e) {
      setResult({ status: "failed", error: e?.response?.data?.detail || "Request failed" });
    } finally { setBusy(false); }
  };
  return (
    <div className={`border p-5 ${cfg.configured ? "bg-white border-[#E8E1D2]" : "bg-[#FDF7E6] border-[#E8D08A]"}`} data-testid="email-config-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">Where notifications go · this server</div>
        <button type="button" onClick={runTest} disabled={busy || !cfg.configured} data-testid="email-test-button"
          className="inline-flex items-center gap-2 bg-[#0F1825] hover:bg-[#C84C21] disabled:opacity-50 text-white text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1.5 transition-colors">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send test notification
        </button>
      </div>
      {result && (
        <div className={`mt-3 border px-3 py-2 text-[12px] ${result.status === "sent" ? "bg-[#EAF3EC] border-[#CFE2D2] text-[#3F6D55]" : "bg-[#FBE7DF] border-[#F2C9B6] text-[#C84C21]"}`} data-testid="email-test-result">
          {result.status === "sent"
            ? `Test email sent to ${result.to.join(", ")} via ${result.provider === "managed" ? "managed email" : result.provider}. Check the inbox (and spam) in the next minute.`
            : `Not delivered${result.to?.length ? ` to ${result.to.join(", ")}` : ""}: ${result.error}`}
        </div>
      )}
      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
        <div>
          <div className="text-[#A09680] text-[11px]">Recipients</div>
          <div className="text-[#0F1825]" data-testid="email-config-to">{cfg.to?.length ? cfg.to.join(", ") : "— none configured —"}</div>
        </div>
        <div>
          <div className="text-[#A09680] text-[11px]">Sent as</div>
          <div className="text-[#0F1825]">{cfg.from_name}{cfg.from_email ? ` · ${cfg.from_email}` : ""}</div>
        </div>
        <div>
          <div className="text-[#A09680] text-[11px]">Channel</div>
          <div className="text-[#0F1825]" data-testid="email-config-provider">
            {cfg.host || "—"}{cfg.provider === "managed" && cfg.smtp_fallback ? " · SMTP fallback" : ""}{cfg.configured ? "" : " · not configured"}
          </div>
        </div>
      </div>
      {!cfg.configured && (
        <div className="mt-3 text-[12px] text-[#9A6B1A]">
          Email is not configured on this deployment — requests are saved here but no notification is sent. Set EMERGENT_EMAIL_KEY · EMAIL_FROM_NAME · CONTACT_TO in the deployment environment.
        </div>
      )}
    </div>
  );
}

function RequestCard({ s }) {
  const when = new Date(s.created_at);
  return (
    <article className="bg-white border border-[#E8E1D2] p-6" data-testid={`request-${s.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-serif text-xl text-[#0F1825]">{s.name} <span className="text-[#A09680]">·</span> <span className="text-[#5C6470] text-[15px]">{s.company}</span></div>
          <div className="mt-1 text-[12.5px] text-[#5C6470]">
            <a href={`mailto:${s.email}`} className="underline decoration-[#E8E1D2] hover:text-[#C84C21]">{s.email}</a>
            {s.role && <> · {s.role}</>}
            {s.interest_area && <> · <span className="font-mono text-[10px] uppercase tracking-[0.14em]">{s.interest_area}</span></>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">{when.toLocaleString()}</div>
          <div className="mt-1.5"><StatusChip s={s} /></div>
        </div>
      </div>
      <p className="mt-4 text-[14px] text-[#0F1825] leading-relaxed whitespace-pre-wrap">{s.message}</p>
      {(s.email_to || s.email_error) && (
        <div className="mt-3 pt-3 border-t border-[#F0EBDC] font-mono text-[10px] text-[#A09680] tracking-[0.06em]">
          {s.email_to && <div>To: {s.email_to}{s.email_provider ? ` · via ${s.email_provider === "managed" ? "managed email" : s.email_provider}` : ""}</div>}
          {s.email_error && <div className="text-[#C84C21] mt-0.5" data-testid={`request-error-${s.id}`}>Error: {s.email_error}</div>}
        </div>
      )}
    </article>
  );
}

export default function ConsoleRequests() {
  const [items, setItems] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([axios.get(`${API}/contacts`), axios.get(`${API}/contacts/email-config`)])
      .then(([a, b]) => { setItems(a.data); setCfg(b.data); })
      .catch(() => setErr("Could not load briefing requests."));
  }, []);

  return (
    <ConsoleShell breadcrumb={<Breadcrumb items={[{ label: "Engagements", to: "/console/engagements" }, { label: "Briefing requests" }]} />}>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="page-console-requests">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C84C21]">Inbound · voice2evidence.com/contact</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0F1825] mt-3 leading-[1.1] tracking-tight">Briefing requests.</h1>
          <p className="mt-4 text-[#5C6470] text-[15px] leading-relaxed max-w-2xl">
            Every form submitted on the public site lands here first, whether or not the email notification went through.
          </p>
        </div>

        <EmailConfigPanel cfg={cfg} />

        {err && <div className="mt-6 text-[#C84C21] text-[13px]" data-testid="requests-error">{err}</div>}
        {!items && !err && <div className="mt-8 flex items-center gap-2 text-[#5C6470] text-[13px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
        {items && items.length === 0 && (
          <div className="mt-8 bg-white border border-[#E8E1D2] p-10 text-center text-[#5C6470]" data-testid="requests-empty">
            <Inbox size={22} className="mx-auto text-[#A09680]" />
            <div className="mt-3">No briefing requests yet.</div>
          </div>
        )}
        {items && items.length > 0 && (
          <>
            <div className="mt-8 mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#A09680] flex items-center gap-2" data-testid="requests-count">
              <Mail size={12} /> {items.length} request{items.length === 1 ? "" : "s"} · newest first
            </div>
            <div className="space-y-4">{items.map((s) => <RequestCard key={s.id} s={s} />)}</div>
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
